import { FC } from "react";
import { Separator } from "types/separator/separator.type";
import {
  getSeparatorLabel,
  getSeparatorTypeLabel,
  getShape,
  isOilSeparator,
} from "types/separator/separator.helpers";
import { JaNejStatus } from "types/enums";
import {
  DetailsList,
  Item,
  Label,
  Value,
} from "components/detailsList/DetailsList";
import { HeaderContainer, ShadowCard } from "components/ShadowCard";
import { useUpdateSeparatorLogic } from "hooks/useUpdateSeparatorLogic";
import { Headline4 } from "components/typography/Headline4";
import SecondaryButton from "components/buttons/SecondaryButton";
import Modal from "components/Modal";
import { EditSeparatorTechnicalInformation } from "./EditSeparatorTechnicalInformation";

interface Props {
  separator: Separator;
}
export const AvskiljareTechnicalInformation: FC<Props> = ({ separator }) => {
  const updateSeparatorTechnicalInformationLogic =
    useUpdateSeparatorLogic(separator);
  return (
    <ShadowCard className="max-w-5xl">
      <HeaderContainer>
        <Headline4>Teknisk information</Headline4>
        <SecondaryButton
          size="small"
          onClick={updateSeparatorTechnicalInformationLogic.openModal}
        >
          Ändra
        </SecondaryButton>
      </HeaderContainer>
      <DetailsList>
        {getTechnicalInformationFieldsForSeparator(separator).map(
          ({ label, value }) => (
            <Item key={label}>
              <Label>{label}</Label>
              <Value>{value}</Value>
            </Item>
          ),
        )}
      </DetailsList>
      <Modal
        isOpen={updateSeparatorTechnicalInformationLogic.isModalOpen}
        title={"Ändra objekt"}
        onClose={updateSeparatorTechnicalInformationLogic.onModalClose}
        actionHandler={
          updateSeparatorTechnicalInformationLogic.onSeparatorSubmit
        }
        actionText="Spara"
        isLoading={updateSeparatorTechnicalInformationLogic.isUpdating}
        loadingText="Sparar..."
      >
        <EditSeparatorTechnicalInformation
          register={updateSeparatorTechnicalInformationLogic.register}
          formErrors={updateSeparatorTechnicalInformationLogic.formErrors}
          formData={updateSeparatorTechnicalInformationLogic.formData}
        />
      </Modal>
    </ShadowCard>
  );
};

export const getTechnicalInformationFieldsForSeparator = (
  separator: Separator,
) =>
  [
    {
      label: getSeparatorLabel("type"),
      value: getSeparatorTypeLabel(separator),
    },
    {
      label: getSeparatorLabel("marking858"),
      value: onlyKeepJaNejAnswer(separator.marking858),
    },
    {
      label: getSeparatorLabel("verification858"),
      value:
        separator.verification858 === JaNejStatus.Ja
          ? `Ja - ${separator.verification858Text}`
          : "Nej",
    },
    {
      label: getSeparatorLabel("classification"),
      value:
        separator.classification === "Unavailable"
          ? getSeparatorLabel("separatorClassificationMissing")
          : separator.classification,
      oilOnly: true,
    },
    { label: getSeparatorLabel("manufacturer"), value: separator.manufacturer },
    {
      label: getSeparatorLabel("yearOfManufacture"),
      value: separator.yearOfManufacture,
    },
    {
      label: getSeparatorLabel("modelIdentifier"),
      value: separator.modelIdentifier,
    },
    { label: getSeparatorLabel("shape"), value: getShape(separator) },
    {
      label: getSeparatorLabel("totalVatskevolym"),
      value: !!separator.totalVatskevolym
        ? `${separator.totalVatskevolym} m³`
        : "",
    },
    {
      label: getSeparatorLabel("slamMaxLevel"),
      value: !!separator.slamMaxLevel ? `${separator.slamMaxLevel} mm` : "",
    },
    {
      label: getSeparatorLabel("maxSlamvolym"),
      value: !!separator.maxSlamvolym ? `${separator.maxSlamvolym} m³` : "",
    },
    {
      label: getSeparatorLabel("maxOljevolym"),
      value: !!separator.maxOljevolym ? `${separator.maxOljevolym} m³` : "",
      oilOnly: true,
    },
    {
      label: getSeparatorLabel("flode"),
      value: !!separator.flode ? `${separator.flode} l/s` : "",
      oilOnly: true,
    },
    {
      label: getSeparatorLabel("oilMaxLevel"),
      value: !!separator.oilMaxLevel ? `${separator.oilMaxLevel} mm` : "",
      oilOnly: true,
    },
    {
      label: getSeparatorLabel("provtagningsbrunnFinns"),
      value: onlyKeepJaNejAnswer(separator.provtagningsbrunnFinns),
    },
    {
      label: getSeparatorLabel("koalecensfilterFinns"),
      value: onlyKeepJaNejAnswer(separator.koalecensfilterFinns),
      oilOnly: true,
    },
    {
      label: getSeparatorLabel("nivalarmFinns"),
      value: onlyKeepJaNejAnswer(separator.nivalarmFinns),
      oilOnly: true,
    },
    {
      label: getSeparatorLabel("slamnivalarmFinns"),
      value: onlyKeepJaNejAnswer(separator.slamnivalarmFinns),
    },
    {
      label: getSeparatorLabel("damningslarmHognivalarmFinns"),
      value: onlyKeepJaNejAnswer(separator.damningslarmHognivalarmFinns),
      oilOnly: true,
    },
    {
      label: getSeparatorLabel("flottarFinns"),
      value: onlyKeepJaNejAnswer(separator.flottarFinns),
      oilOnly: true,
    },
    {
      label: getSeparatorLabel("flottarDensitetAngiven"),
      value: !!separator.flottarDensitetAngiven
        ? `${separator.flottarDensitetAngiven} kg/dm`
        : "",
      oilOnly: true,
    },
  ]
    .filter(
      (item) =>
        item.label !== getSeparatorLabel("flottarDensitetAngiven") ||
        separator.flottarFinns === JaNejStatus.Ja,
    )
    .filter((item) => isOilSeparator(separator) || !item.oilOnly);

export const getDetailedSeparatorFields = (separator: Separator) => [
  ...getTechnicalInformationFieldsForSeparator(separator),
];

const onlyKeepJaNejAnswer = (item?: JaNejStatus) =>
  !item ? "" : item === JaNejStatus.Ej_Angett ? "" : item.toString();
